import { DrawerProps, FormDrawer, FormDrawerContent, Id, isSuccess } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { useFormikContext } from "formik";
import { FC, forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { PredefinedFilterApplier } from "../contactFilter/PredefinedFilterApplier";
import { contactFilterService } from "../shared/api/contactFilterService";
import { useContactAttributeOptions } from "../shared/hooks/useContactAttributeOptions";
import { ContactFilterFormValues, ContactFilterSaveRequest } from "../shared/types";
import { useContactFilterValidation } from "./ContactFilterDrawer";
import { ContactFilterDrawerFilterItems } from "./ContactFilterDrawerFilterItems";
import { useContactFilterQuery } from "./ContactFilterQuery";
import { ContactFilterSelectControl } from "./ContactFilterSelectControl";
import { mapFormValuesToRequest, mapResourceToFormValues } from "./mappers";

const emptyFilter: ContactFilterFormValues = {
	translations: [],
	type: "onDemand",
	filterItems: [],
	showInSidebar: false,
};

interface Props extends DrawerProps {
	contactFilterId: Id | null;
	onApply: (contactFilterId: Id | null) => void;
}

const DirtyRef = forwardRef<boolean>(function DirtyRefComponent(_, ref) {
	const { dirty } = useFormikContext();
	useImperativeHandle(ref, () => dirty, [dirty]);

	return null;
});

export const ContactFilterApplyDrawer: FC<Props> = ({ onApply, contactFilterId, ...drawerProps }) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const dirtyRef = useRef(false);
	const [predefinedFilterId, setPredefinedFilterId] = useState(contactFilterId);
	const { contactAttributeOptions } = useContactAttributeOptions();
	const validate = useContactFilterValidation({ requiresNameField: false });

	const { data: predefinedFilter } = useContactFilterQuery({ contactFilterId: predefinedFilterId });

	const initialFormValues = useMemo(() => {
		if (predefinedFilter == null) {
			return emptyFilter;
		}
		return mapResourceToFormValues(predefinedFilter);
	}, [predefinedFilter]);

	const handleSubmit = useCallback(
		async (formValues: ContactFilterFormValues) => {
			if (!dirtyRef.current && predefinedFilterId) {
				onApply(predefinedFilterId);
				return true;
			}

			if (formValues.filterItems.length === 0) {
				onApply(null);
				return true;
			}

			const request: ContactFilterSaveRequest = {
				filterItems: mapFormValuesToRequest(formValues, contactAttributeOptions).filterItems,
				type: "onDemand",
				translations: [],
				showInSidebar: false,
			};
			const result = await contactFilterService.store(request);
			if (isSuccess(result)) {
				const contactFilterIdFromResponse = result.data.data.id;
				onApply(contactFilterIdFromResponse);
				void queryClient.invalidateQueries({
					queryKey: contactFilterService.baseOptionsKey,
					refetchType: "inactive",
				});
				void queryClient.invalidateQueries({ queryKey: contactFilterService.showKey(contactFilterIdFromResponse) });
				return true;
			}
			return false;
		},
		[predefinedFilterId, contactAttributeOptions, onApply, queryClient],
	);

	return (
		<FormDrawer
			{...drawerProps}
			title={t("Contact filter")}
			initialValues={initialFormValues}
			validate={validate}
			onSubmit={handleSubmit}
			onClose={() => drawerProps.onClose?.()}
			renderForm={(props) => (
				<FormDrawerContent {...props} submitButtonTitle={t("Apply")}>
					<DirtyRef ref={dirtyRef} />
					<PredefinedFilterApplier filter={predefinedFilter || null} />
					<ContactFilterSelectControl label={t("Predefined filter")} onSelect={setPredefinedFilterId} />
					<ContactFilterDrawerFilterItems />
				</FormDrawerContent>
			)}
		/>
	);
};
